"use client"

import React from "react"

import {
  Box,
  CONTACT_PHONE_HREF,
  HR,
  P,
  TextButton,
  TextLink,
  useIsSm,
} from "@bullseye/components"
import { SUPPORT_EMAIL } from "@bullseye/routes"
import { colors } from "@bullseye/tailwind-config"

import {
  ACTIVITIES_URI,
  PRIVACY_POLICY_URI,
  TERMS_OF_SERVICE_URI,
} from "../../config/route-path"
import Facebook from "../../icons/facebook"
import Instagram from "../../icons/instagram"

const Footer: React.FC = () => {
  const isMobileViewport = useIsSm()
  const footerLinks = [{ name: "Activities", path: ACTIVITIES_URI }]

  const sections = [
    <>
      <P className="pb-6" bold size="xs">
        WHAT WE PROVIDE
      </P>
      {footerLinks.map((r) => (
        <Box className="pb-6" key={r.name}>
          <TextLink href={r.path} label={r.name} />
        </Box>
      ))}
    </>,
    <>
      <P className="pb-6" bold size="xs">
        COMPANY INFO
      </P>
      {[
        { name: "Careers", path: "/company/careers" },
        { name: "About us", path: "/company/about" },
        { name: "Blog", path: "/blog" },
        { name: "FAQ", path: "/company/faq" },
        { name: "Community Guidelines", path: "/company/community-guidelines" },
      ].map((r) => (
        <Box className="pb-6" key={r.name}>
          <TextLink href={r.path} label={r.name} />
        </Box>
      ))}
    </>,
    <>
      <P className="pb-6" bold size="xs">
        CONTACT US
      </P>
      <Box className="flex flex-row justify-start pb-6">
        <TextButton
          className="py-3"
          startIconType="mail"
          as="a"
          href={`mailto:${SUPPORT_EMAIL}`}
        >
          {SUPPORT_EMAIL}
        </TextButton>
      </Box>
      <Box className="flex flex-row justify-start pb-6">
        <TextButton
          className="py-3"
          startIconType="phone"
          as="a"
          href={`tel:${CONTACT_PHONE_HREF}`}
        >
          Call {CONTACT_PHONE_HREF}
        </TextButton>
      </Box>
      <Box className="inline pb-6">
        Press Inquiries:{" "}
        <TextLink href={`mailto:${SUPPORT_EMAIL}`} label={SUPPORT_EMAIL} />
      </Box>
      <Box className="pb-6">
        <TextLink href="https://help.gethank.com" label="Help center" />
      </Box>
      <Box className="flex flex-row items-center pb-6">
        <img
          src="https://storage.googleapis.com/hank-app-assets/marketing/AARP%20Portfolio%20Company%20Badge_On%20dark.png"
          alt="AARP Badge"
          width={120}
          height={120}
        />
        <img height="96px" src="/images/bbb_logo.png" alt="Better Business Bureau" />
      </Box>
    </>,
  ]

  return (
    <footer className="border-t border-solid border-gray-100 bg-gray-10">
      <Box className="w-full px-4 pb-20 md:px-32 md:pb-0">
        <Box className="flex justify-center border-solid border-gray-100 pt-4 md:flex-row md:border-none md:pt-12">
          {sections.map((section, idx) => (
            <Box
              key={idx}
              className="flex-1 border-b border-solid border-gray-100 pt-6 md:border-none md:pt-0"
            >
              {section}
            </Box>
          ))}
        </Box>
        <Box className="hidden pt-8 md:flex">
          <HR />
        </Box>
        <Box className="flex justify-between pb-5 pt-8 md:flex-row md:items-center md:pt-6">
          <Box className="flex md:flex-row md:items-center">
            {[
              {
                title: `© ${new Date().getFullYear()} Hank, Inc.`,
                path: "/legal/copyright",
              },
              {
                title: "Terms of Service",
                path: TERMS_OF_SERVICE_URI,
              },
              {
                title: "Privacy Policy",
                path: PRIVACY_POLICY_URI,
              },
            ].map((loc, idx) => (
              <React.Fragment key={loc.title}>
                {!isMobileViewport && idx !== 0 && (
                  <P className="px-2">&middot;</P>
                )}
                <Box className="pb-6 md:pb-0">
                  <a
                    style={{
                      textDecoration: "none",
                      color: colors.gray[950],
                      fontSize: "0.875rem",
                    }}
                    href={loc.path}
                  >
                    {loc.title}
                  </a>
                </Box>
              </React.Fragment>
            ))}
          </Box>
          <Box className="flex flex-row items-center gap-4">
            <a
              href="https://www.facebook.com/gethankapp"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center"
            >
              <Facebook />
            </a>
            <a
              href="https://www.instagram.com/get.hank"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center"
            >
              <Instagram />
            </a>
          </Box>
        </Box>
      </Box>
    </footer>
  )
}

export default Footer
